import { Callout } from "@bednbody/ui/components/callout";
import { emergencyGuaranteeTopUp, type RiderRank } from "@bednbody/logic";
import { Button } from "@bednbody/ui/components/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@bednbody/ui/components/dialog";
import { RadioGroup, RadioGroupItem } from "@bednbody/ui/components/radio-group";
import { Textarea } from "@bednbody/ui/components/textarea";
import { useState } from "react";
import type { GuaranteeQueueRow } from "../rider-sessions.hooks";

interface GuaranteeReviewDialogProps {
  session: GuaranteeQueueRow | null;
  isPending: boolean;
  errorMessage: string | null;
  onClose: () => void;
  onSubmit: (input: { decision: "approved" | "declined"; reason: string }) => void;
}

/**
 * RIDER-028 — the emergency guarantee decision.
 *
 * Three things this dialog says out loud rather than implying:
 *
 * 1. **Who may do this is undefined.** The rule says "authorized Operations"
 *    and the vault never names the role, so the capability is superadmin-only
 *    and every use is audited (never-invent-policy.md §2).
 * 2. **The amount is not the reviewer's.** It used to be typed in, pre-filled
 *    with a flat ₱600, and payroll (0126) paid it on top of the Session's
 *    earnings. CORE-07 §4.5 defines the guarantee as the top-up to ₱600, so the
 *    admin Edge Function computes it from the settled basis (0220) and this
 *    dialog only previews the same figure with the same function.
 * 3. **An approval IS paid** — by the next payroll run. See the warning below.
 */
export function GuaranteeReviewDialog({
  session,
  isPending,
  errorMessage,
  onClose,
  onSubmit,
}: GuaranteeReviewDialogProps) {
  const [decision, setDecision] = useState<"approved" | "declined">("approved");
  const [reason, setReason] = useState("");

  // A preview only — the server recomputes from the same basis and ignores any
  // amount the browser could send. Null until the Session's earnings settle.
  const basis =
    session?.settled_guarantee_basis == null ? null : Number(session.settled_guarantee_basis);
  const rank = (session?.rider_rank ?? "bronze") as RiderRank;
  const topUp = basis === null ? null : emergencyGuaranteeTopUp(basis, rank);
  const rankEligible = rank === "gold" || rank === "diamond";

  const canSubmit =
    reason.trim().length > 0 && !isPending && (decision === "declined" || topUp !== null);

  return (
    <Dialog
      open={session !== null}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Guarantee review</DialogTitle>
          <DialogDescription>
            {session
              ? `${session.rider_name} · Session ${session.code} on ${session.service_date} · outcome ${session.outcome?.replace(/_/g, " ") ?? "unknown"}`
              : null}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {session ? (
            <div className="space-y-1 rounded-lg border border-border bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
              {basis === null ? (
                <p>Earnings for this Session have not settled yet.</p>
              ) : (
                <>
                  <p>
                    Settled at{" "}
                    <span className="font-medium text-foreground tabular-nums">
                      ₱{Number(session.settled_total ?? 0).toFixed(2)}
                    </span>{" "}
                    with no guarantee applied.
                  </p>
                  <p>
                    Counted toward the ₱600:{" "}
                    <span className="font-medium text-foreground tabular-nums">
                      ₱{basis.toFixed(2)}
                    </span>{" "}
                    — Dispatch Fees, Peak Boosters and any surcharge or cancellation shares.
                    Overtime is not counted (RIDER-027).
                  </p>
                </>
              )}
              {session.reason ? <p>Reported: {session.reason}</p> : null}
            </div>
          ) : null}

          <fieldset className="space-y-2">
            <legend className="mb-2 block text-sm font-medium">Decision</legend>
            <RadioGroup
              value={decision}
              onValueChange={(value) => setDecision(value as "approved" | "declined")}
            >
              {(
                [
                  ["approved", "Approve the guarantee"],
                  ["declined", "Decline"],
                ] as const
              ).map(([value, label]) => (
                <label
                  key={value}
                  htmlFor={`guarantee-${value}`}
                  className="flex items-center gap-3 rounded-md border border-border px-3 py-2"
                >
                  <RadioGroupItem id={`guarantee-${value}`} value={value} />
                  <span className="text-sm">{label}</span>
                </label>
              ))}
            </RadioGroup>
          </fieldset>

          {decision === "approved" ? (
            <div className="space-y-1">
              <p className="text-sm font-medium">Guarantee paid if approved</p>
              {topUp === null ? (
                <p className="text-xs text-muted-foreground">
                  Can&rsquo;t be worked out until this Session&rsquo;s earnings settle. Declining is
                  still possible now.
                </p>
              ) : (
                <>
                  <p className="text-lg font-semibold tabular-nums">₱{topUp.toFixed(2)}</p>
                  <p className="text-xs text-muted-foreground">
                    {rankEligible
                      ? "₱600 less what already counted toward it (CORE-07 §4.5) — the top-up to ₱600, not ₱600 on top."
                      : `A ${rank} rider is not eligible for the guarantee (RIDER-025), so approving pays nothing.`}{" "}
                    Worked out by the server when you record the decision.
                  </p>
                </>
              )}
            </div>
          ) : null}

          <div className="space-y-2">
            <label htmlFor="guarantee-reason" className="block text-sm font-medium">
              Reason (required)
            </label>
            <Textarea
              id="guarantee-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="What happened, and why this decision?"
              rows={3}
            />
            <p className="text-xs text-muted-foreground">
              Recorded in the audit log with your name. A decision is written once and cannot be
              edited afterwards.
            </p>
          </div>

          {/* ⚠ THIS WARNING WAS INVERTED AND IT WAS A MONEY RISK. It read: "It
              does not change the rider's settled earnings and does not reach
              payroll — paying it out is a separate step that does not exist
              yet." That was 0067's limit, and `0126` ("OQ-77: an approved
              emergency guarantee finally pays the Rider") closed it by teaching
              the payroll read about the decision. An operator who believed the
              old copy would go and arrange a manual payment — i.e. pay the
              rider twice. */}
          <Callout tone="warning">
            An approval is paid automatically by the next payroll run (it is read at payroll time,
            not written back to the settled row). Do not arrange a separate payment.
          </Callout>

          {errorMessage ? <p className="text-sm text-destructive">{errorMessage}</p> : null}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isPending}>
            Cancel
          </Button>
          <Button
            onClick={() => onSubmit({ decision, reason: reason.trim() })}
            disabled={!canSubmit}
          >
            {isPending ? "Recording…" : "Record decision"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
